import { useEffect, useRef } from "react";
import { useStore } from "react-redux";
import { useLazyDownloadArchiveQuery } from "features/fileSystem/filesStorage";
import { removeBrowserZipJob } from "features/browserZipSlice";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch, useAppSelector } from "features/store";
import type { RootState } from "features/store";
import {
  isDownloadArchiveError,
  isDownloadArchivePresigned,
} from "types/interfaces";
import {
  downloadZipFromPresignedEntries,
  type PresignedZipEntry,
} from "utils/browserZipDownload";

export default function BrowserZipJobPoller() {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;

  const [triggerDownloadArchive] = useLazyDownloadArchiveQuery();

  const jobsRecord = useAppSelector((s) => s.browserZip.jobs);
  const inFlightRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const jobs = store.getState().browserZip.jobs;
    const keys = Object.keys(jobs);

    for (const itemKey of keys) {
      if (inFlightRef.current.has(itemKey)) continue;
      const job = jobs[itemKey];
      if (!job) continue;

      inFlightRef.current.add(itemKey);

      void (async () => {
        const label = job.label;
        let successMessage: string | null = null;
        let errorMessage: string | null = null;

        try {
          let entries: PresignedZipEntry[] = [];
          let filename: string;

          if (job.kind === "from_entries") {
            entries = job.file_entries;
            filename = job.filename;
          } else {
            let response = await triggerDownloadArchive({
              source: job.source,
              fileName: label,
            }).unwrap();

            if (isDownloadArchiveError(response)) {
              errorMessage = response.error;
              return;
            }
            if (!isDownloadArchivePresigned(response)) {
              errorMessage = "Unexpected response format for folder download";
              return;
            }

            entries = [...response.file_entries];
            filename = response.filename || `${label}.zip`;
            let nextToken: string | null | undefined = response.next_token;

            while (nextToken) {
              const nextResponse = await triggerDownloadArchive({
                source: job.source,
                fileName: label,
                next_token: nextToken,
              }).unwrap();

              if (isDownloadArchiveError(nextResponse)) {
                errorMessage = nextResponse.error;
                return;
              }
              if (isDownloadArchivePresigned(nextResponse)) {
                entries = entries.concat(nextResponse.file_entries);
                nextToken = nextResponse.next_token;
              } else {
                errorMessage = "Unexpected paginated folder download response";
                return;
              }
            }
          }

          const result = await downloadZipFromPresignedEntries(entries, filename);
          if (result.ok) {
            successMessage = `ZIP for "${label}" download started.`;
          } else {
            errorMessage = result.message;
          }
        } catch {
          errorMessage = "ZIP build failed";
        } finally {
          inFlightRef.current.delete(itemKey);
          dispatchRef.current(removeBrowserZipJob({ itemKey }));

          if (successMessage) {
            dispatchRef.current(
              pushNotification({
                message: successMessage,
                severity: "success",
              }),
            );
          }
          if (errorMessage) {
            dispatchRef.current(
              pushNotification({
                message: errorMessage,
                severity: "error",
              }),
            );
          }
        }
      })();
    }
  }, [jobsRecord, store, triggerDownloadArchive]);

  return null;
}
